/**
 * Tenant-wide milestone template editor. Rendered inside Settings for
 * super_admin only.
 *
 * Changes here propagate to every construction project in the tenant
 * (the PUT endpoint on the server rebuilds each row's milestone array
 * while preserving completed states + assignees for keys that survive).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Loader2,
  Edit2,
  GripVertical,
  RotateCcw,
  Save,
  AlertTriangle,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";

// Slug helper — turns "Ölçüm Turu" → "olcum_turu" so we always have a
// stable, ASCII-only DB key. Kept identical to the frontend detail
// page's version so the two surfaces don't diverge.
const _slug = (s) =>
  (s || "")
    .toLowerCase()
    .replaceAll("ı", "i")
    .replaceAll("ö", "o")
    .replaceAll("ü", "u")
    .replaceAll("ş", "s")
    .replaceAll("ç", "c")
    .replaceAll("ğ", "g")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "unnamed";

// Chip tints mirrored from the detail page for visual continuity.
const GROUP_TINT = {
  preparation:  "bg-sky-50 text-sky-800 border-sky-200",
  design:       "bg-violet-50 text-violet-800 border-violet-200",
  quote:        "bg-indigo-50 text-indigo-800 border-indigo-200",
  contract:     "bg-emerald-50 text-emerald-800 border-emerald-200",
  production:   "bg-amber-50 text-amber-800 border-amber-200",
  shipping:     "bg-cyan-50 text-cyan-800 border-cyan-200",
  installation: "bg-rose-50 text-rose-800 border-rose-200",
  handover:     "bg-teal-50 text-teal-800 border-teal-200",
};

export default function MilestoneTemplateEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Snapshot of what's on the server so we can Reset without reloading.
  const [serverItems, setServerItems] = useState([]);
  // Inline edit target: `{scope: "group"|"item", key, value}`.
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/settings/milestone-template");
        setItems(res.data.items || []);
        setServerItems(res.data.items || []);
      } catch (err) {
        toast.error("Şablon yüklenemedi");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const markDirty = () => setDirty(true);

  const groups = (() => {
    const acc = [];
    let cur = null;
    items.forEach((m, idx) => {
      if (!cur || cur.key !== m.group) {
        cur = { key: m.group || "misc", label: m.group_label || "Diğer", items: [] };
        acc.push(cur);
      }
      cur.items.push({ ...m, __idx: idx });
    });
    return acc;
  })();

  const addGroup = () => {
    const label = window.prompt("Yeni grup adı:");
    if (!label || !label.trim()) return;
    const slug = _slug(label);
    const existingGroups = new Set(items.map((m) => m.group));
    let groupKey = `group_${slug}`;
    let n = 1;
    while (existingGroups.has(groupKey)) groupKey = `group_${slug}_${++n}`;
    const firstItem = window.prompt(
      `"${label.trim()}" grubunun ilk alt kategorisi:`,
      label.trim()
    );
    if (!firstItem || !firstItem.trim()) return;
    setItems((prev) => [
      ...prev,
      {
        key: `${groupKey}_${_slug(firstItem)}`,
        label: firstItem.trim(),
        group: groupKey,
        group_label: label.trim(),
      },
    ]);
    markDirty();
  };

  const addSubItem = (groupKey, groupLabel) => {
    const label = window.prompt(`"${groupLabel}" grubuna yeni alt kategori adı:`);
    if (!label || !label.trim()) return;
    const slug = _slug(label);
    const existingKeys = new Set(items.map((m) => m.key));
    let key = `${groupKey}_${slug}`;
    let n = 1;
    while (existingKeys.has(key)) key = `${groupKey}_${slug}_${++n}`;
    // Insert right after the last item of that group so the tree keeps
    // its ordering intact.
    setItems((prev) => {
      const lastIdx = prev
        .map((m, i) => (m.group === groupKey ? i : -1))
        .filter((i) => i >= 0)
        .pop();
      const insertAt = lastIdx === undefined ? prev.length : lastIdx + 1;
      const next = [...prev];
      next.splice(insertAt, 0, {
        key,
        label: label.trim(),
        group: groupKey,
        group_label: groupLabel,
      });
      return next;
    });
    markDirty();
  };

  const deleteItem = (msKey) => {
    const target = items.find((m) => m.key === msKey);
    if (!target) return;
    if (!window.confirm(`"${target.label}" alt kategorisi tüm projelerden silinsin mi?`)) return;
    setItems((prev) => prev.filter((m) => m.key !== msKey));
    markDirty();
  };

  const deleteGroup = (groupKey) => {
    const affected = items.filter((m) => m.group === groupKey);
    if (affected.length === 0) return;
    if (
      !window.confirm(
        `"${affected[0].group_label}" grubu ve ${affected.length} alt kategorisi tüm projelerden silinsin mi?`
      )
    ) return;
    setItems((prev) => prev.filter((m) => m.group !== groupKey));
    markDirty();
  };

  const commitEdit = () => {
    if (!editing || !editing.value.trim()) {
      setEditing(null);
      return;
    }
    const newLabel = editing.value.trim();
    setItems((prev) => {
      if (editing.scope === "group") {
        return prev.map((m) =>
          m.group === editing.key ? { ...m, group_label: newLabel } : m
        );
      }
      return prev.map((m) => (m.key === editing.key ? { ...m, label: newLabel } : m));
    });
    setEditing(null);
    markDirty();
  };

  const reset = () => {
    setItems(serverItems);
    setDirty(false);
    setEditing(null);
  };

  // --- DnD ---------------------------------------------------------
  // Two levels: outer sortable list for groups (dnd-id = "group:<key>")
  // and per-group inner list for sub-items (dnd-id = "item:<key>"). We
  // only allow reorder within the same scope; cross-scope drops fall
  // through with no state change.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const reorderGroups = (activeGroupKey, overGroupKey) => {
    // Rebuild `items` so the whole group's items land in the new
    // position without splitting them. We map each group to its item
    // slice, arrayMove the group order, then flatten again.
    const byGroup = new Map();
    for (const m of items) {
      if (!byGroup.has(m.group)) byGroup.set(m.group, []);
      byGroup.get(m.group).push(m);
    }
    const currentOrder = Array.from(byGroup.keys());
    const from = currentOrder.indexOf(activeGroupKey);
    const to = currentOrder.indexOf(overGroupKey);
    if (from === -1 || to === -1 || from === to) return;
    const nextOrder = arrayMove(currentOrder, from, to);
    setItems(nextOrder.flatMap((k) => byGroup.get(k)));
    markDirty();
  };

  const reorderInGroup = (groupKey, activeItemKey, overItemKey) => {
    const groupItems = items.filter((m) => m.group === groupKey);
    const from = groupItems.findIndex((m) => m.key === activeItemKey);
    const to = groupItems.findIndex((m) => m.key === overItemKey);
    if (from === -1 || to === -1 || from === to) return;
    const nextGroupItems = arrayMove(groupItems, from, to);
    // Splice the reordered slice back into `items` at the position of
    // this group's first occurrence.
    const firstIdx = items.findIndex((m) => m.group === groupKey);
    const rest = items.filter((m) => m.group !== groupKey);
    const next = [
      ...rest.slice(0, firstIdx),
      ...nextGroupItems,
      ...rest.slice(firstIdx),
    ];
    // The naive splice above can double-count when the group is not at
    // the start. Simpler + robust: walk items in order, replacing the
    // matching group's entries with the reordered version.
    const rebuilt = [];
    let placed = false;
    for (const m of items) {
      if (m.group === groupKey) {
        if (!placed) {
          rebuilt.push(...nextGroupItems);
          placed = true;
        }
      } else {
        rebuilt.push(m);
      }
    }
    setItems(rebuilt);
    markDirty();
    // `next` was only used for debugging the earlier attempt; discard it.
    void next;
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items.map((m) => ({
          key: m.key,
          label: m.label,
          group: m.group,
          group_label: m.group_label,
        })),
      };
      const res = await api.put("/settings/milestone-template", payload);
      setServerItems(res.data.items || items);
      setDirty(false);
      toast.success(`Şablon kaydedildi · ${res.data.propagated_to} projeye yansıtıldı`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kaydedilemedi";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div
      className="max-w-3xl space-y-4 rounded-lg border border-zinc-200 bg-white p-6"
      data-testid="milestone-template-editor"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Kilometre Taşı Şablonu</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Burada yaptığın değişiklikler tenant&apos;taki tüm inşaat projelerine
            anında yansır. Tamamlanmış check&apos;ler ve kişi atamaları korunur.
          </p>
        </div>
        <div className="flex gap-2">
          {dirty && (
            <Button
              variant="outline"
              size="sm"
              onClick={reset}
              data-testid="milestone-template-reset"
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Geri al
            </Button>
          )}
          <Button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="milestone-template-save"
            className="bg-violet-600 text-white hover:bg-violet-700"
          >
            {saving ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-3.5 w-3.5" />
            )}
            {dirty ? "Kaydet & tüm projelere yansıt" : "Kaydedildi"}
          </Button>
        </div>
      </div>

      {dirty && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Kaydedilmemiş değişiklikler var. Kaydettiğinde her proje otomatik güncellenir.
          </span>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(evt) => {
          const { active, over } = evt;
          if (!over || active.id === over.id) return;
          const [aScope, aKey] = String(active.id).split(":");
          const [oScope, oKey] = String(over.id).split(":");
          if (aScope !== oScope) return; // don't allow cross-scope drops
          if (aScope === "group") {
            reorderGroups(aKey, oKey);
          } else if (aScope === "item") {
            // Find the group of the active item to constrain the move.
            const src = items.find((m) => m.key === aKey);
            if (src) reorderInGroup(src.group, aKey, oKey);
          }
        }}
      >
        <SortableContext
          items={groups.map((g) => `group:${g.key}`)}
          strategy={verticalListSortingStrategy}
        >
          <ol className="space-y-3" data-testid="milestone-template-list">
            {groups.map((g) => (
              <SortableGroup
                key={g.key}
                group={g}
                editing={editing}
                setEditing={setEditing}
                commitEdit={commitEdit}
                deleteGroup={deleteGroup}
                deleteItem={deleteItem}
                addSubItem={addSubItem}
                tint={GROUP_TINT[g.key] || "bg-zinc-50 text-zinc-800 border-zinc-200"}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        onClick={addGroup}
        data-testid="tpl-add-group"
        className="inline-flex items-center gap-2 rounded-lg border border-dashed border-violet-300 bg-violet-50/50 px-4 py-2 text-[13px] font-medium text-violet-700 transition-colors hover:bg-violet-100"
      >
        <Plus className="h-4 w-4" />
        Yeni grup ekle
      </button>
    </div>
  );
}


function SortableGroup({
  group: g,
  editing,
  setEditing,
  commitEdit,
  deleteGroup,
  deleteItem,
  addSubItem,
  tint,
}) {
  const isEditingGroup = editing?.scope === "group" && editing.key === g.key;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `group:${g.key}` });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`tpl-group-${g.key}`}
      className="overflow-hidden rounded-xl border border-zinc-200 bg-white"
    >
      <div className={`flex items-center gap-2 border-b px-4 py-2 ${tint}`}>
        {/* Group drag handle — only the grip is draggable so clicks
            elsewhere in the header (rename, delete) still work. */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          data-testid={`tpl-group-drag-${g.key}`}
          className="cursor-grab p-0.5 opacity-50 hover:opacity-100 active:cursor-grabbing"
          aria-label="Grubu sürükle"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        {isEditingGroup ? (
          <input
            autoFocus
            value={editing.value}
            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") setEditing(null);
            }}
            data-testid={`tpl-group-edit-${g.key}`}
            className="flex-1 rounded-md border border-current/30 bg-white/10 px-2 py-0.5 text-[13px] font-semibold uppercase tracking-wider outline-none"
          />
        ) : (
          <span className="flex-1 text-[13px] font-semibold uppercase tracking-wider">
            {g.label}
          </span>
        )}
        <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-semibold tabular-nums">
          {g.items.length}
        </span>
        <button
          type="button"
          onClick={() => setEditing({ scope: "group", key: g.key, value: g.label })}
          data-testid={`tpl-group-rename-${g.key}`}
          title="Adı değiştir"
          className="rounded p-1 hover:bg-black/10"
        >
          <Edit2 className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={() => deleteGroup(g.key)}
          data-testid={`tpl-group-delete-${g.key}`}
          title="Grubu sil"
          className="rounded p-1 text-rose-700 hover:bg-rose-100"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>

      {/* Inner sortable list of sub-items */}
      <SortableContext
        items={g.items.map((m) => `item:${m.key}`)}
        strategy={verticalListSortingStrategy}
      >
        <div className="divide-y divide-zinc-100">
          {g.items.map((m) => (
            <SortableItem
              key={m.key}
              milestone={m}
              editing={editing}
              setEditing={setEditing}
              commitEdit={commitEdit}
              deleteItem={deleteItem}
            />
          ))}
          <button
            type="button"
            onClick={() => addSubItem(g.key, g.label)}
            data-testid={`tpl-add-item-${g.key}`}
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-[12px] font-medium text-violet-600 transition-colors hover:bg-violet-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Alt kategori ekle
          </button>
        </div>
      </SortableContext>
    </li>
  );
}

function SortableItem({ milestone: m, editing, setEditing, commitEdit, deleteItem }) {
  const isEditingItem = editing?.scope === "item" && editing.key === m.key;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `item:${m.key}` });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`tpl-item-${m.key}`}
      className="flex items-center gap-2 bg-white px-4 py-2 text-sm"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`tpl-item-drag-${m.key}`}
        className="cursor-grab p-0.5 text-zinc-300 hover:text-zinc-600 active:cursor-grabbing"
        aria-label="Sürükle"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      {isEditingItem ? (
        <input
          autoFocus
          value={editing.value}
          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitEdit();
            if (e.key === "Escape") setEditing(null);
          }}
          data-testid={`tpl-item-edit-${m.key}`}
          className="flex-1 rounded-md border border-zinc-300 px-2 py-1 text-[13px] outline-none focus:border-violet-400"
        />
      ) : (
        <span className="flex-1 text-[13px] text-zinc-800">{m.label}</span>
      )}
      <button
        type="button"
        onClick={() =>
          setEditing({ scope: "item", key: m.key, value: m.label })
        }
        data-testid={`tpl-item-rename-${m.key}`}
        title="Adı değiştir"
        className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
      >
        <Edit2 className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={() => deleteItem(m.key)}
        data-testid={`tpl-item-delete-${m.key}`}
        title="Sil"
        className="rounded p-1 text-rose-400 hover:bg-rose-50 hover:text-rose-700"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}
